import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Lock } from "lucide-react";
import { verifyStorefrontGate, unlockStorefrontGate } from "../services/storefront-web-service";

export function StorefrontPasswordGate({
  organizationId,
  storeName,
  onUnlocked,
  verify,
}: {
  organizationId: string;
  storeName: string;
  onUnlocked: () => void;
  // Optional override for password verification. The default uses the
  // JWT-bound `ecommerce_verify_storefront_gate` RPC for authenticated
  // BMS previews. The public storefront supplies a host-based variant that
  // uses an `anon`-callable RPC parameterised by `organization_id`.
  verify?: (password: string) => Promise<boolean>;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const ok = verify ? await verify(password) : await verifyStorefrontGate(password);
      if (ok) {
        unlockStorefrontGate(organizationId);
        onUnlocked();
      } else {
        setError("Incorrect password. Try again or ask your store administrator.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
            <Lock className="h-6 w-6 text-indigo-600" />
          </div>
          <CardTitle className="text-xl">{storeName}</CardTitle>
          <CardDescription>This storefront is password-protected. Enter the access password to continue.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="storefront-gate-pw">Access password</Label>
              <Input
                id="storefront-gate-pw"
                type="password"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <Button type="submit" className="w-full" disabled={loading || !password.trim()}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
